import type {
  FooterLink,
  Footer_GroupedSection,
  GroupedSection,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/footer.types';
import {GetFooterStaticResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {Entry, LegalEntry, SectionWithLinks} from '@aboutyou/ui-components';
import {exists} from '@aboutyou/utils';
import {ContactsSectionProps} from '../FooterStatic/ContactsSection';
import {handleOpenConsentOptions} from './openConsentOptions';

export const mapContactInfo = (
  entries?: GetFooterStaticResponse['footer']['contactInfo'],
) =>
  (entries ?? [])
    .filter(entry => entry?.type?.$case !== 'socialLink')
    .map(entry => {
      switch (entry.type?.$case) {
        case 'phone': {
          return {
            type: 'PHONE' as const,
            label: entry.type.phone.label,
            href: entry.type.phone.href,
          };
        }
        case 'chat': {
          return {
            type: 'CHAT' as const,
            label: entry.type.chat.label,
            href: entry.type.chat.href,
          };
        }
        case 'vipWhatsappPhone': {
          return {
            type: 'VIP_WHATSAPP' as const,
            label: entry.type.vipWhatsappPhone.label,
            href: entry.type.vipWhatsappPhone.href,
          };
        }
        default:
          return null;
      }
    })
    .filter(item => !!item) as ContactsSectionProps['contactInfoEntries'];

export const mapSocialLinks = (
  entries?: GetFooterStaticResponse['footer']['contactInfo'],
) =>
  (entries ?? [])
    .map(entry =>
      entry.type?.$case === 'socialLink' ? entry.type.socialLink : null,
    )
    .filter(exists) as ContactsSectionProps['socialLinks'];

const getHref = (link: FooterLink) =>
  link.action?.$case === 'href' ? link.action.href.url : undefined;

const getClickAction = (link: FooterLink) =>
  link.action?.$case === 'openConsentOptions'
    ? handleOpenConsentOptions
    : undefined;

export function mapLinkToEntry(links: FooterLink[]): Entry[] {
  return links.map(link => ({
    label: link.label?.text || link.deprecatedLabel,
    href: getHref(link) || '',
  }));
}

export function mapLinkToLegalEntry(links: FooterLink[]): LegalEntry[] {
  return links.map(link => ({
    label: link.label?.text || link.deprecatedLabel,
    href: getHref(link),
    onClick: getClickAction(link),
  }));
}

export function mapSectionToEntry(
  section: GetFooterStaticResponse['footer']['linksSections'],
): SectionWithLinks[] {
  return section.map(section => ({
    title: section.title,
    links: mapLinkToEntry(section.links),
  }));
}

export function mapGroupedSectionToEntry(
  section: (Footer_GroupedSection | GroupedSection)[],
): SectionWithLinks[] {
  return section.map(section => {
    if (typeof section.title === 'string') {
      return {
        title: {
          label: section.title,
          href: '/',
        } as Entry,
        links: mapLinkToEntry(section.links as FooterLink[]),
      };
    }

    const titleLabel = section.title.label;
    return {
      title: {
        label:
          typeof titleLabel === 'string'
            ? titleLabel
            : titleLabel.text ?? (section.title as FooterLink).deprecatedLabel,
        href: getHref(section.title as FooterLink),
      } as Entry,
      links: mapLinkToEntry(section.links as FooterLink[]),
    };
  });
}
